import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { IResearchPodcastPopulated } from '~global-interfaces/IResearchPodcast';

import { CompanylistService } from '~features/company/companylist.service';
import { PodcastService } from '~features/podcasts/podcast.service';
import { UserBehaviorAnalyticsService } from '~features/user-behavior-analytics/user-behavior-analytics.service';
import { PosthogService } from '~shared/services/posthog.service';

import { AudioTrackingConfig, AudioTrackingDirective } from '../audio-tracking.directive';

@Component({
  selector: 'app-podcast-mini-player',
  imports: [AudioTrackingDirective],
  templateUrl: './podcast-mini-player.component.html',
  standalone: true,
})
export class PodcastMiniPlayerComponent implements OnDestroy, OnInit {
  @Input() downloadURL: string;
  @Input() fileURL: string;
  @Input() pdfURL: string;
  @Input() podcast: IResearchPodcastPopulated;
  @Input() hideLinkArrows = false;

  podcastService = inject(PodcastService);
  posthogService = inject(PosthogService);
  ubaService = inject(UserBehaviorAnalyticsService);
  companylistService = inject(CompanylistService);

  trackingConfig: AudioTrackingConfig;
  private trackedProgressMilestones = new Set<number>();

  constructor() {
    this.trackingConfig = {
      podcastTitle: '',
      companyTicker: '',
      researchDate: '',
      companySlug: '',
    };
  }

  ngOnInit() {
    if (this.podcast) {
      this.trackingConfig = {
        podcastTitle: this.podcast.title,
        companyTicker: this.podcast.companyTicker,
        researchDate: this.podcast.research_date,
        companySlug: this.companylistService.getSlugByCompanyTicker(this.podcast.companyTicker),
      };
    }
  }

  copyShareLink(podcast) {
    this.podcastService.copyShareLinkToClipBoard(podcast);
  }

  onPlay(event: { currentTime: number }) {
    // Reset progress tracking when starting a new session
    this.trackedProgressMilestones.clear();

    this.posthogService.trackPodcastPlayed(this.podcast.companyTicker, this.podcast.research_date);

    this.ubaService.trackButtonClick(
      'play-podcast',
      this.trackingConfig.companySlug,
      {
        podcastTitle: this.trackingConfig.podcastTitle,
        companyTicker: this.trackingConfig.companyTicker,
        researchDate: this.trackingConfig.researchDate,
        action: 'play',
        currentTime: event.currentTime,
      },
      window.location.href
    );
  }

  onPause(event: { currentTime: number; sessionPlayTime: number; totalPlayTime: number }) {
    this.ubaService.trackButtonClick(
      'pause-podcast',
      this.trackingConfig.companySlug,
      {
        podcastTitle: this.trackingConfig.podcastTitle,
        companyTicker: this.trackingConfig.companyTicker,
        researchDate: this.trackingConfig.researchDate,
        action: 'pause',
        sessionPlayTime: event.sessionPlayTime,
        totalPlayTime: event.totalPlayTime,
        currentTime: Math.round(event.currentTime),
      },
      window.location.href
    );
  }

  onEnded(event: { totalPlayTime: number }) {
    this.ubaService.trackButtonClick(
      'podcast-completed',
      this.trackingConfig.companySlug,
      {
        podcastTitle: this.trackingConfig.podcastTitle,
        companyTicker: this.trackingConfig.companyTicker,
        researchDate: this.trackingConfig.researchDate,
        action: 'completed',
        totalPlayTime: event.totalPlayTime,
        completed: true,
      },
      window.location.href
    );
  }

  onTimeUpdate(event: { currentTime: number; duration: number }) {
    // Track progress every 10 seconds to avoid spam
    const currentTimeSeconds = Math.round(event.currentTime);
    const progressMilestone = Math.floor(currentTimeSeconds / 10) * 10;

    // Only track if we haven't already tracked this milestone and we're past 0 seconds
    if (progressMilestone > 0 && !this.trackedProgressMilestones.has(progressMilestone)) {
      this.trackedProgressMilestones.add(progressMilestone);

      this.ubaService.trackButtonClick(
        'podcast-listening-progress',
        this.trackingConfig.companySlug,
        {
          podcastTitle: this.trackingConfig.podcastTitle,
          companyTicker: this.trackingConfig.companyTicker,
          researchDate: this.trackingConfig.researchDate,
          action: 'listening_progress',
          currentTime: currentTimeSeconds,
          duration: Math.round(event.duration),
          progress: Math.round((event.currentTime / event.duration) * 100),
        },
        window.location.href
      );
    }
  }

  onDownloadResearch() {
    this.ubaService.trackButtonClick(
      'download-research',
      this.trackingConfig.companySlug,
      {
        podcastTitle: this.trackingConfig.podcastTitle,
        companyTicker: this.trackingConfig.companyTicker,
        researchDate: this.trackingConfig.researchDate,
        action: 'download_research',
        fileType: 'pdf',
      },
      window.location.href
    );
  }

  ngOnDestroy() {
    // Component cleanup is handled by the directive
  }
}
